import { NORMAL_FONT_SIZE, EXTRA_SMALL_FONT_SIZE } from '../utils/fontSizes';
import { d3 } from '.';

const DECREMENT = 2;
const LINE_HEIGHT = 1.1;
const specialChars = /\/$|-$|,$/g; // characters to wordwrap on: , - /
const whitespaces = /[^\S\u00A0]/g; // all whitespace characters except for nbsp
const nbsp = /\u00A0$/g;

// From lodash: Creates an array with all falsy values removed. The values `false`, `null`,
// `0`, `""`, `undefined`, and `NaN` are falsy.
function compact(array: any[]): any[] {
  let index = -1;
  const length = array ? array.length : 0;
  let resIndex = 0;
  const result = [];

  while (++index < length) {
    const value = array[index];
    if (value) {
      result[resIndex++] = value;
    }
  }
  return result;
}

// Compare strings without whitespace
function stringsNotEqual(str1: string = '', str2: string = '') {
  return str1.replace(/\s/g, '') !== str2.replace(/\s/g, '');
}

interface ShouldUpdateParams<
  GElement extends d3.BaseType = d3.BaseType,
  Datum = unknown,
  PElement extends d3.BaseType = d3.BaseType,
  PDatum = unknown,
> {
  selection: d3.Selection<GElement, Datum, PElement, PDatum>;
  texts: string[];
  maxWidth: number;
  maxHeight: number;
}

/**
 *
 * Re-wrap text if:
 * 1. text for this item changed since last update
 * 2. text box width or height changed since last update
 * 3. items were added or removed since last update
 *
 * @param selection: d3 selection of text elements
 * @param texts: list of strings for each selection.text()
 * @param maxWidth: width of text box
 * @param maxHeight: height of text box
 *
 * @returns boolean
 */
export function shouldUpdateText<
  GElement extends d3.BaseType = d3.BaseType,
  Datum = unknown,
  PElement extends d3.BaseType = d3.BaseType,
  PDatum = unknown,
>({
  selection,
  texts,
  maxWidth,
  maxHeight,
}: ShouldUpdateParams<GElement, Datum, PElement, PDatum>): boolean {
  let update = false;
  const numItems = texts.length;

  selection.each(function (_, i) {
    const that = this as any;
    const text = d3.select(this).text();
    // Check criteria for re-wrapping text
    const shouldUpdate =
      maxWidth !== that._previousMaxWidth ||
      maxHeight !== that._previousMaxHeight ||
      numItems !== that._previousNumItems ||
      stringsNotEqual(text, texts[i]);

    // Store values to compare on next update
    that._previousMaxWidth = maxWidth;
    that._previousMaxHeight = maxHeight;
    that._previousNumItems = numItems;

    // Set flag for function return
    if (shouldUpdate) {
      update = true;
    }
  });

  return update;
}

interface WrapParams {
  maxWidth: number;
  maxHeight: number;
  fontSize?: number;
  verticallyAlignMiddle?: boolean;
}

export const splitText = (text: string): string[] => {
  return text
    .replace(/- /g, '-\u00A0') // Add a space after "-" with RegEx to avoid split
    .replace(/-+/g, '$& ') // Auto line break
    .replace(/, /g, ',\u00A0') // Add a space after "," with RegEx to avoid split
    .replace(/,+/g, '$& ') // Auto line break
    .replace(/\/ /g, '/\u00A0') // Add a space after "/" with RegEx to avoid split
    .replace(/\/+/g, '$& ') // Auto line break
    .split(whitespaces) // Removes any spaces after "-"
    .reverse();
};

export const smartJoin = (line: string[]): string => {
  let output = '';
  for (let i = 0; i < line.length; i++) {
    if (
      specialChars.test(output) === true ||
      nbsp.test(output) === true ||
      i === 0
    ) {
      // If we find a special character we want to join without a space, otherwise we end up with "potatis- skalare" instead of "potatis-skalare"
      // The first word in the line of words should be treated the same, hence, i === 0
      output = output + line[i];
    } else {
      output = `${output} ${line[i]}`;
    }
  }
  return output;
};

/**
 * Tries to fit a text in to the area of width and height specified.
 *
 * @param texts: d3 selection of text elements
 * @param maxWidth: the maximum width of the text
 * @param maxHeight: the maximum height of the text
 * @param fontSize: the starting font size (NOTE: Try and keep low, since many iterations takes time)
 * @param verticallyAlignMiddle: vertically center text when spanning more than one line
 */
export function wordWrap<
  GElement extends d3.BaseType = d3.BaseType,
  Datum = unknown,
  PElement extends d3.BaseType = d3.BaseType,
  PDatum = unknown,
>(
  texts: d3.Selection<GElement, Datum, PElement, PDatum>, // An array of D3 text elements
  {
    maxWidth,
    maxHeight,
    fontSize = NORMAL_FONT_SIZE,
    verticallyAlignMiddle = false,
  }: WrapParams,
): void {
  let size = fontSize;
  const originalTexts: Record<keyof any, string[]> = {};

  function buildTextSpans<
    GElement extends d3.BaseType = d3.BaseType,
    Datum = unknown,
    PElement extends d3.BaseType = d3.BaseType,
    PDatum = unknown,
  >(
    text: d3.Selection<GElement, Datum, PElement, PDatum>,
    originalText: string[],
  ) {
    text.attr('font-size', `${size}px`);
    text.selectAll('tspan').remove();
    const words = originalText.slice(0);
    let word;
    let line = new Array<string>();
    const x = text.attr('x') || 0;
    const y = text.attr('y') || 0;
    const dy = parseFloat(text.attr('dy')) || 0;
    let tspan = text
      .attr('y', y)
      .text(null)
      .append('tspan')
      .attr('x', x)
      .attr('dy', `${dy}em`);
    try {
      while ((word = words.pop())) {
        line.push(word);
        tspan.text(smartJoin(line));
        const node = tspan.node();
        if (!node) {
          continue;
        }
        const textWidth = node.getComputedTextLength();
        if (textWidth > maxWidth && line.length > 1) {
          line.pop();
          tspan.text(smartJoin(line));
          line = [word];
          tspan = text
            .append('tspan')
            .attr('x', x)
            .attr('dy', `${LINE_HEIGHT}em`)
            .text(word);
        }
      }
    } catch (_) {
      // Error: "Unexpected call to method or property access" on getComputedTextLength() in Edge, this error
      // supposedly occurs when the element is not in the visible DOM.
      // This error does not effect the user, so it can be swallowed
    }
  }

  const wrap = function (this: SVGGraphicsElement, _: never, i: keyof any) {
    const text = d3.select(this);
    do {
      buildTextSpans.call<
        GElement,
        Parameters<typeof buildTextSpans<GElement, Datum>>,
        void
      >(
        this as unknown as GElement,
        text as unknown as d3.Selection<GElement, Datum, null, undefined>,
        originalTexts[i] ?? [],
      );
      if (
        this.getBBox().height > maxHeight ||
        this.getBBox().width > maxWidth
      ) {
        size -= DECREMENT;
      }
    } while (
      (this.getBBox().height > maxHeight || this.getBBox().width > maxWidth) &&
      size > EXTRA_SMALL_FONT_SIZE
    );
    text.attr('font-size', `${size}px`);
  };

  // Wrap each text individually, deciding the correct font size
  texts.each(function (_, i) {
    // insert space after hyphen groups and then split on space
    const text = splitText(d3.select(this).text());
    originalTexts[i] = compact(text);
    if (size > EXTRA_SMALL_FONT_SIZE) {
      wrap.apply(
        this as unknown as SVGGraphicsElement,
        arguments as unknown as Parameters<typeof wrap>, // eslint-disable-line prefer-rest-params
      );
    }
  });

  texts.each(function (_, i) {
    const text = d3.select<GElement, Datum>(this);

    // Build the correct tspan structure with the assigned font size
    buildTextSpans.call<
      GElement,
      Parameters<typeof buildTextSpans<GElement, Datum>>,
      void
    >(this, text, originalTexts[i] ?? []);

    const childrenLength = (text.node() as unknown as SVGTextElement).childNodes
      .length;
    const shouldCenterVertically = verticallyAlignMiddle && childrenLength > 1;
    if (shouldCenterVertically) {
      text.attr('y', `${-LINE_HEIGHT * (childrenLength - 1)}em`);
    }
  });
}
